import { Descriptions, Input } from 'antd';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { getVanSetting, updateVanSetting } from '../../api/campuses.api';
import Button from '../../components/Button';
import ErrorResponse from '../../components/ErrorResponse';
import SimpleHeader from '../../components/layout/SimpleHeader';
import Loader from '../../components/Loader';
import ConfirmModal from '../../components/modals/ConfirmModal';
import SelectCampus from '../../components/SelectCampus';
import useAuth from '../../hooks/useAuth';
import useAxios from '../../hooks/useAxios';
import useNotification from '../../hooks/useNotification';
import { IGetVanSetting, IVanSetting } from '../../models/campuses.model';
import { RootState } from '../../store/rootReducer';
import Radio from '../../components/Radio';

const CampusVanSetting = () => {
  const { t, i18n } = useTranslation();
  const { writeAuthYn } = useAuth();
  const { currentCampus } = useSelector((state: RootState) => state.auth);
  const { successNotification } = useNotification();
  const { data: res, isLoading, error, execute } = useAxios<IGetVanSetting>();
  const { data: submitRes, isLoading: submitIsLoading, error: submitError, execute: submitExecute } = useAxios();

  const [items, setItems] = useState<IVanSetting[]>([]);
  const [confirmSubmitVisible, setConfirmSubmitVisible] = useState(false);

  const handleInputChange = (index: number, value: string, field: string) => {
    setItems(prevItems =>
      prevItems.map((item, i) =>
        i === index ? { ...item, [field]: value } : item,
      ),
    );
  };

  useEffect(() => {
    if (currentCampus) {
      execute(getVanSetting(currentCampus));
    }
  }, [currentCampus, submitIsLoading]);

  useEffect(() => {
    if (res?.data) {
      setItems((prevItems) => {
        const newItems = [...res.data]; // 새로운 데이터를 복사하여 상태 업데이트
        return JSON.stringify(prevItems) !== JSON.stringify(newItems) ? newItems : prevItems;
      });
    }
  }, [res]); // isLoading 제거

  useEffect(() => {
    if (submitRes) {
      setConfirmSubmitVisible(false);
      successNotification(t('common.message.save1'));
    }
  }, [submitRes]);

  const onSubmit = async () => {
    const req = {
      catIds: items.map(it => ({ catId: it.catId, epayCatId: it.epayCatId, detailCode: it.detailCode, useYn: it.useYn || 'N'})),
    };

    submitExecute(updateVanSetting(currentCampus, req));
  };

  return (
    <>
      <div className="content scrollbar">
        {isLoading || submitIsLoading ? <Loader /> : null}
        <ErrorResponse error={error} />
        <ErrorResponse error={submitError} />

        <SimpleHeader
          iconFile="icon-campus-big.png"
          parentMenu={t('common.menu.campus')}
          currentMenu={t('VAN 설정')}
          onlyTitle={true}
        />

        <div className="comm-table__area rowType">
          <Descriptions bordered size="small" column={1} labelStyle={{ width: '25%' }}>
            <Descriptions.Item label={t('common.menu.campus')}>
              <div className="w-150p">
                <SelectCampus />
              </div>
            </Descriptions.Item>
          </Descriptions>
        </div>

        <div className="comm-table__area rowType">
          <table>
            <thead>
            <tr>
              <th>계정</th>
              <th>CAT ID</th>
              <th>EPAY CAT ID</th>
              <th>과세 여부</th>
            </tr>
            </thead>
            <tbody>
            {items.map((it, index) => (
              <tr key={index}>
                <td>{i18n.language === 'ko' ? it.detailCodeName : it.detailCodeEngName}</td>

                <td>
                  <Input
                    type="text"
                    value={it.catId || ''}
                    onChange={(e) => handleInputChange(index, e.target.value, 'catId')}
                  />
                </td>

                <td>
                  <Input
                    type="text"
                    value={it.epayCatId || ''}
                    onChange={(e) => handleInputChange(index, e.target.value, 'epayCatId')}
                  />
                </td>

                <td className="text-center">
                  {it.useYn ?? (['AL', 'PT'].includes(it.detailCode) ? 'Y' : 'N')}
                </td>
              </tr>
            ))}
            </tbody>
          </table>

          <div className="comm-bx__space">
            <div className="comm-btn__area right">
              <Button
                onClick={() => setConfirmSubmitVisible(true)}
                className="btn-basic pink"
                text={t('common.button.save')}
                writeAuthYn={writeAuthYn}
              />
            </div>
          </div>
        </div>

        <ConfirmModal
          visible={confirmSubmitVisible}
          setVisible={setConfirmSubmitVisible}
          onConfirm={onSubmit}
          titleText={t('VAN 설정 저장')}
          messageText={t('campus.setting.text7')}
          confirmText={t('common.button.save')}
          cancelText={t('common.button.cancel')}
        />
      </div>
    </>
  );
};

export default CampusVanSetting;
